import { z } from "zod";
import { ProductEntitySchema } from "./product";

/**
 * 쇼핑몰 실시간 상담 채널 (우측 하단 플로팅 버튼, 택1).
 *
 * company-shop.ts 가 이 모듈을 import 하므로 enum 은 낮은 쪽인 여기에 둔다.
 * 반대로 두면 zod 스키마(값)의 순환 import 가 된다. company-shop.ts 는
 * 기존 import 경로를 유지하려고 이걸 re-export 한다.
 */
export const ConsultChannelTypeSchema = z.enum([
  "NONE",
  "CHANNEL_TALK",
  "KAKAO_CHANNEL",
]);

export type ConsultChannelType = z.infer<typeof ConsultChannelTypeSchema>;

/**
 * GET /api/shop 응답의 layoutSpecification.
 *
 * 이 스키마가 레이아웃 스펙의 단일 진실이다. 예전에는 shared/types/api.ts 에
 * 같은 모양의 interface 가 따로 선언돼 있어서 한쪽만 고치면 소리 없이 어긋났다
 * (실제로 designProductEnabled / channelTalkEnabled 가 그렇게 양쪽에 남아 있었다).
 * api.ts 는 이제 이 타입을 import 해서 re-export 만 한다.
 */
export const ShopLayoutSpecificationSchema = z.object({
  isCarouselLayout: z.boolean(),
  designProductSlideInterval: z.number().nullable().optional(),
  bestSellerEnabled: z.boolean(),
  recentlyViewedEnabled: z.boolean(),
  consultChannelType: ConsultChannelTypeSchema.default("NONE"),
});

export type ShopLayoutSpecification = z.infer<
  typeof ShopLayoutSpecificationSchema
>;

export const ProductLightDtoSchema = z.object({
  id: z.number(),
  thumbnail: z.string().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  price: z.number(),
});

export type ProductLightDto = z.infer<typeof ProductLightDtoSchema>;

export const CategoryLightDtoSchema = z.object({
  id: z.number(),
  name: z.string(),
  products: z.array(ProductLightDtoSchema),
});

export type CategoryLightDto = z.infer<typeof CategoryLightDtoSchema>;

export const SocialLinkDtoSchema = z.object({
  instagramUrl: z.string().nullable().optional(),
  facebookUrl: z.string().nullable().optional(),
  youtubeUrl: z.string().nullable().optional(),
  naverBlogUrl: z.string().nullable().optional(),
  kakaoChannelUrl: z.string().nullable().optional(),
});

export type SocialLinkDto = z.infer<typeof SocialLinkDtoSchema>;

export const AdditionalServiceDtoSchema = z.object({
  channelTalkKey: z.string().nullable().optional(),
  tossPayKey: z.string().nullable().optional(),
});

export type AdditionalServiceDto = z.infer<typeof AdditionalServiceDtoSchema>;

export const ShopDetailDtoSchema = z.object({
  id: z.number(),
  shopName: z.string(),
  shopLogoUrl: z.string(),

  companyId: z.number(),
  companyName: z.string(),
  ceoName: z.string(),
  personalInfoManagerName: z.string(),
  address: z.string(),
  contact: z.string(),
  businessNumber: z.string(),
  ecommerceBizNumber: z.string(),

  layoutSpecification: ShopLayoutSpecificationSchema,
  categories: CategoryLightDtoSchema,
  socialLink: SocialLinkDtoSchema,
  additionalServices: AdditionalServiceDtoSchema,
});

export type ShopDetailDto = z.infer<typeof ShopDetailDtoSchema>;

export const GetCategoriesResponseSchema = z.array(
  z.object({
    id: z.number(),
    companyId: z.number(),
    name: z.string(),
    createdAt: z.string(),
    updatedAt: z.string().nullable().optional(),
    products: z.array(ProductEntitySchema),
  }),
);

export type GetCategoriesResponse = z.infer<typeof GetCategoriesResponseSchema>;

export const GetRecentlyViewedProductsResponseSchema = z.array(
  z.object({
    id: z.number(),
    companyId: z.number(),
    categoryId: z.number(),
    name: z.string(),
    description: z.string().nullable().optional(),
    thumbnail: z.string().nullable().optional(),
    price: z.number(),
    createdAt: z.string(),
    updatedAt: z.string().nullable().optional(),
  }),
);

export type GetRecentlyViewedProductsResponse = z.infer<
  typeof GetRecentlyViewedProductsResponseSchema
>;
